import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter } from 'react-router-dom'
import App from './App'
import { ErrorBoundary } from './components/ErrorBoundary'
import './index.css'
import { initCache } from './db/bootstrap'
import { exec, query, batch } from './db/client'

if (import.meta.env.DEV) {
  ;(window as unknown as { __db: unknown }).__db = {
    query,
    exec,
    batch,
    tables: () =>
      query("SELECT name FROM sqlite_master WHERE type='table' ORDER BY name"),
    schema: (table: string) =>
      query(`SELECT sql FROM sqlite_master WHERE name = ?`, [table]),
    count: (table: string) => query(`SELECT COUNT(*) as n FROM ${table}`),
  }
  console.log(
    '[db] dev helpers available: __db.query, __db.exec, __db.batch, __db.tables(), __db.count("transactions")',
  )
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: false, refetchOnWindowFocus: false },
  },
})

initCache().catch((err) => {
  console.warn('Local cache unavailable; falling back to API-only mode:', err)
})

// Swallow lightweight-charts v5's sporadic "Value is null" hit-test errors.
// They originate inside the library's own mouse-move handler (not in our
// React tree), but in dev React picks them up as window errors and trips the
// nearest error boundary, blanking the chart. The user can't do anything
// about them and a moment later the next hover succeeds — so we drop them
// at the source and let the chart keep rendering.
function isLightweightChartsHitTestError(stack: string | undefined, message: string): boolean {
  if (!stack) return false
  if (!stack.includes('lightweight-charts')) return false
  // The crashing path is always: ensureNotNull → SeriesBarColorer → hitTest.
  return message.includes('Value is null') && stack.includes('hitTest')
}

window.addEventListener(
  'error',
  (event) => {
    const err = event.error as Error | undefined
    if (isLightweightChartsHitTestError(err?.stack, event.message)) {
      event.preventDefault()
      event.stopImmediatePropagation()
    }
  },
  true,
)
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason as Error | undefined
  if (isLightweightChartsHitTestError(reason?.stack, reason?.message ?? '')) {
    event.preventDefault()
  }
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>,
)
